import { doorCopy, type DoorColourName, type DoorTypeId } from '@planner/config';
import type { ManufacturedSize } from '@planner/shared/doors';
import { colourPresentation } from '../../appdata.js';
import type { DoorPrice } from '../lib/catalogue.js';
import { formatPrice } from '../lib/format.js';
import type { LockEvaluation } from '../lib/lock.js';
import type { LockDraft } from '../stores/doorStore.js';

export type DoorPreviewPrice = DoorPrice | { status: 'checking' } | { status: 'unverified' };

interface PreviewProps {
  step: 'lock' | 'colour';
  doorType: DoorTypeId;
  frameColour: DoorColourName;
  size: ManufacturedSize;
  lock: LockDraft;
  evaluation: LockEvaluation;
  price: DoorPreviewPrice;
}

export function doorPreviewImage({
  doorType,
  frameColour,
  lock,
}: Pick<PreviewProps, 'doorType' | 'frameColour' | 'lock'>) {
  const colour = colourPresentation.colours.find((item) => item.name === frameColour);
  if (!colour) {
    throw new Error(`Missing preview for frame colour ${frameColour}`);
  }
  const photo = colour.photos[doorType];
  return {
    ...photo,
    alt: lock.side ? `${photo.alt}, ${lock.side}-hand handle` : photo.alt,
    mirrored: lock.side !== null && lock.side !== colourPresentation.photoFacing[doorType],
  };
}

function priceLabel(price: DoorPreviewPrice): string {
  if (price.status === 'checking') {
    return 'Checking…';
  }
  if (price.status === 'unavailable' || price.status === 'unverified') {
    return 'Unavailable';
  }
  return `${price.status === 'from' ? 'From ' : ''}${formatPrice(price.price)}`;
}

function priceDescription(price: DoorPreviewPrice, step: PreviewProps['step']): string {
  if (price.status === 'checking') {
    return 'Checking catalogue availability.';
  }
  if (price.status === 'unverified') {
    return 'Price could not be verified. Try again.';
  }
  if (price.status === 'from') {
    return 'Price depends on colour. GST included; confirmed at order.';
  }
  if (price.status === 'estimate') {
    return step === 'colour'
      ? 'GST included; confirmed at order.'
      : 'Indicative Black price. GST included; confirmed at order.';
  }
  return step === 'colour'
    ? 'No price is available for this colour and size.'
    : 'No indicative price is available for this size.';
}

function handleHeight(lock: LockDraft, evaluation: LockEvaluation): string {
  return lock.height.trim()
    ? `${lock.height} mm${evaluation.heightError ? ' (check value)' : ''}`
    : '—';
}

function handleSide(lock: LockDraft): string {
  return lock.side ? `${lock.side[0].toUpperCase()}${lock.side.slice(1)}` : '—';
}

function PreviewContents(props: PreviewProps) {
  const { step, frameColour, size, lock, evaluation, price } = props;
  const image = doorPreviewImage(props);
  const rows = [
    { label: 'Height', value: `${size.height} mm` },
    { label: 'Width', value: `${size.width} mm` },
    { label: 'Handle Height', value: handleHeight(lock, evaluation) },
    { label: 'Handle Side', value: handleSide(lock) },
    ...(step === 'colour' ? [{ label: 'Colour', value: frameColour }] : []),
  ];

  return (
    <>
      <div className="mb-5 flex h-[300px] items-center justify-center rounded-lg bg-page px-2 py-4 lg:h-[425px]">
        <img
          data-name={`${step}-preview-image`}
          src={image.src}
          alt={image.alt}
          style={{ transform: image.mirrored ? 'scaleX(-1)' : undefined }}
          className="h-full w-full object-contain"
        />
      </div>
      <p className="mb-2 text-xs font-semibold text-gray-800">Door we&apos;ll make</p>
      <div className="mb-4 overflow-hidden rounded-lg border border-gray-100">
        {rows.map((row, index) => (
          <div
            key={row.label}
            className={`flex items-center justify-between gap-3 px-3 py-2 text-xs ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
          >
            <span className="text-gray-400">{row.label}</span>
            <span className="break-all text-right font-medium text-gray-800">{row.value}</span>
          </div>
        ))}
      </div>
      <div className="border-t border-gray-100 pt-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-gray-400">Estimated price</span>
          <span className="text-xl font-bold text-progress">{priceLabel(price)}</span>
        </div>
        <p className="mt-1 text-xs text-gray-500">{priceDescription(price, step)}</p>
      </div>
    </>
  );
}

export function DesktopDoorPreview(props: PreviewProps) {
  return (
    <aside
      data-name={`${props.step}-preview`}
      className="sticky top-4 hidden self-start rounded-xl border border-gray-200 bg-white p-5 lg:block"
    >
      <PreviewContents {...props} />
    </aside>
  );
}

export function MobileDoorPreview(props: PreviewProps & { depth: string }) {
  const image = doorPreviewImage(props);
  const colour = colourPresentation.colours.find((item) => item.name === props.frameColour);
  const rows = [
    { label: 'Type', value: doorCopy[props.doorType].title },
    { label: 'Height', value: `${props.size.height} mm` },
    { label: 'Width', value: `${props.size.width} mm` },
    ...(props.depth.trim() ? [{ label: 'Depth Clearance', value: `${props.depth} mm` }] : []),
    { label: 'Handle Height', value: handleHeight(props.lock, props.evaluation) },
    { label: 'Handle Side', value: handleSide(props.lock) },
    ...(props.step === 'colour' ? [{ label: 'Colour', value: props.frameColour }] : []),
  ];
  return (
    <details className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white lg:hidden">
      <summary
        data-name={`${props.step}-summary-toggle`}
        className="group min-h-11 cursor-pointer list-none px-4 py-3 text-left text-xs font-medium text-gray-700 hover:bg-gray-50/50 [&::-webkit-details-marker]:hidden"
      >
        <span className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2">
            {props.step === 'colour' && colour && (
              <span
                aria-hidden="true"
                className="size-4 shrink-0 rounded border border-black/10"
                style={{ backgroundColor: colour.hex }}
              />
            )}
            <span>{doorCopy[props.doorType].title}</span>
            <span className="text-gray-400">
              {props.size.width}×{props.size.height} mm
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <span className="flex flex-col items-end">
              {props.step === 'colour' && (
                <span className="text-[10px] font-normal text-gray-500">Estimated price</span>
              )}
              <span className="text-sm font-bold text-gray-900">{priceLabel(props.price)}</span>
            </span>
            <svg
              aria-hidden="true"
              className="size-4 text-gray-400 transition-transform group-open:rotate-180"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </span>
        </span>
        {props.step === 'colour' && (
          <span className="mt-1 block font-normal text-gray-500">
            {priceDescription(props.price, props.step)}
          </span>
        )}
      </summary>
      <div className="grid grid-cols-2 gap-3 border-t border-gray-100 px-4 py-3 text-xs">
        <div className="col-span-2 flex h-48 items-center justify-center rounded-lg bg-page px-2 py-4">
          <img
            data-name={`${props.step}-mobile-preview-image`}
            src={image.src}
            alt={image.alt}
            style={{ transform: image.mirrored ? 'scaleX(-1)' : undefined }}
            className="h-full w-full object-contain"
          />
        </div>
        {rows.map((row) => (
          <div key={row.label} className="min-w-0">
            <p className="mb-0.5 text-gray-400">{row.label}</p>
            <p className="break-all font-medium text-gray-700">{row.value}</p>
          </div>
        ))}
        {props.step === 'lock' && (
          <p className="col-span-2 text-gray-500">{priceDescription(props.price, props.step)}</p>
        )}
      </div>
    </details>
  );
}
